import { useEffect, useId, useRef, useState } from "react";
import { styled, VisuallyHidden, XStack, YStack } from "tamagui";
import { AlertTriangle as AlertTriangleIcon, FileText as FileTextIcon, Plus as PlusIcon, RotateCw as RotateCwIcon, X as XIcon } from "@tamagui/lucide-icons";
import type { UploadAccept } from "@medbillai/utils/files";
import { formatFileSize } from "../../utils/formatters";
import { Avatar } from "../Avatar";
import { Button } from "../Button";
import { FieldHelperRow, type FieldValidation } from "../FieldShell";
import type { IconComponent } from "../Icon";
import { disabledInteractiveStyles } from "../interactive/styles";
import { Body1, Body3, Caption, TextSpan } from "../Typography";
import { useFileDrop } from "./useFileDrop";

// Re-exported: the accept vocabulary lives with the upload rules in
// @medbillai/utils/files so the picker, the dropzone, and the bundling
// guards share one definition.
export type UploadSlotAccept = UploadAccept;
export type UploadSlotFileStatus = "uploading" | "done" | "error";

/** One file in the slot. The caller owns the upload and feeds status/progress. */
export type UploadSlotFile = {
  /** Stable key for the row; falls back to the index. */
  id?: string;
  name: string;
  status: UploadSlotFileStatus;
  /** 0–100, while uploading. */
  progressPercent?: number;
  /** Byte size shown under the name once done, formatted here (e.g. "128 KB"). */
  sizeBytes?: number;
  errorText?: string;
  /**
   * Whether this row shows the remove control (when `onRemove` is provided).
   * Defaults to true — set false for rows whose file has already left the
   * caller's hands, where removing the row wouldn't remove anything real.
   */
  removable?: boolean;
};
const noop = () => {};

// Mirrors Card's outlined surface but adds a drag-active state Card can't
// express. The frame is a non-interactive container; its controls own the press.
const UploadSlotFrame = styled(YStack, {
  name: "UploadSlot",
  backgroundColor: "$surface2",
  borderColor: "$borderDefault",
  borderWidth: "$border2",
  borderRadius: "$br2",
  paddingVertical: "$sp1.5",
  paddingHorizontal: "$sp2",
  gap: "$sp1.5",
  variants: {
    dragActive: {
      true: {
        borderColor: "$borderAccentStrong",
        backgroundColor: "$bgAccent"
      },
      false: {}
    },
    disabled: {
      true: {
        ...disabledInteractiveStyles,
        borderColor: "$borderDefaultHover"
      },
      false: {}
    },
    // Settled by an answer rather than a file. Recedes rather than greying:
    // the disabled treatment reads as blocked, and this slot is finished.
    answered: {
      true: {
        backgroundColor: "$surface0"
      },
      false: {}
    },
    // Listed after the others so an invalid (empty-required) slot keeps its
    // negative border over the default/disabled treatments.
    invalid: {
      true: {
        borderColor: "$borderNegative"
      },
      false: {}
    }
  } as const,
  defaultVariants: {
    dragActive: false,
    disabled: false,
    answered: false,
    invalid: false
  }
});

// Recedes when the slot is settled or blocked — an answered slot is finished
// and a disabled one is not the member's to act on, so neither should carry the
// weight of a live request.
const SlotTitle = styled(Body1, {
  variants: {
    muted: {
      true: {
        color: "$textSubtle"
      },
      false: {
        color: "$textDefault"
      }
    }
  } as const,
  defaultVariants: {
    muted: false
  }
});

// minHeight keeps every row the same height across states, so a one-line "done"
// row lines up with the taller uploading / error rows.
const FileRowFrame = styled(XStack, {
  alignItems: "center",
  gap: "$sp1",
  padding: "$sp1",
  minHeight: "$si6",
  borderRadius: "$br1",
  borderWidth: "$border2",
  borderColor: "$borderDefault",
  backgroundColor: "$surface2",
  variants: {
    error: {
      true: {
        backgroundColor: "$bgNegative",
        borderColor: "$borderNegative"
      },
      false: {}
    }
  } as const,
  defaultVariants: {
    error: false
  }
});
const FileRow = ({
  file,
  index,
  onRemove,
  onRetry,
  disabled
}: {
  file: UploadSlotFile;
  index: number;
  onRemove?: (file: UploadSlotFile, index: number) => void;
  onRetry?: (file: UploadSlotFile, index: number) => void;
  disabled: boolean;
}) => {
  const isError = file.status === "error";
  const isUploading = file.status === "uploading";
  const progressPercent = typeof file.progressPercent === "number" && Number.isFinite(file.progressPercent) ? Math.min(100, Math.max(0, Math.round(file.progressPercent))) : undefined;
  const subtitle = isUploading ? progressPercent !== undefined ? `Uploading... ${progressPercent}%` : "Uploading..." : isError ? file.errorText : file.sizeBytes !== undefined ? formatFileSize(file.sizeBytes) : undefined;
  return <FileRowFrame error={isError} role={isUploading ? "progressbar" : undefined} aria-label={isUploading ? `Upload progress for ${file.name}` : undefined} aria-valuemin={isUploading ? 0 : undefined} aria-valuemax={isUploading ? 100 : undefined} aria-valuenow={isUploading ? progressPercent : undefined} data-sentry-element="FileRowFrame" data-sentry-component="FileRow" data-sentry-source-file="UploadSlot.tsx">
      {/* On error the avatar's negative surface matches the row's, so only the
          red triangle reads. */}
      <Avatar size="small" shape="square" status={isError ? "negative" : "neutral"} icon={isError ? AlertTriangleIcon : FileTextIcon} loading={isUploading} flexShrink={0} data-sentry-element="Avatar" data-sentry-source-file="UploadSlot.tsx" />

      <YStack flex={1} data-sentry-element="YStack" data-sentry-source-file="UploadSlot.tsx">
        <Body3 color="$textDefault" numberOfLines={1} data-sentry-element="Body3" data-sentry-source-file="UploadSlot.tsx">
          {file.name}
        </Body3>
        {subtitle ? <Caption color="$textSubtle">{subtitle}</Caption> : null}
      </YStack>

      <XStack alignItems="center" data-sentry-element="XStack" data-sentry-source-file="UploadSlot.tsx">
        {isError && onRetry ? <Button icon={RotateCwIcon} variant="tertiary" size="small" aria-label={`Retry ${file.name}`} disabled={disabled} onPress={() => onRetry(file, index)} /> : null}
        {/* No remove control mid-upload: removing wouldn't abort the in-flight
            request, so the file would just reappear once it lands. */}
        {onRemove && !isUploading && (file.removable ?? true) ? <Button icon={XIcon} variant="tertiary" size="small" aria-label={`Remove ${file.name}`} disabled={disabled} onPress={() => onRemove(file, index)} /> : null}
      </XStack>
    </FileRowFrame>;
};

// One polite live region announces each upload settling (done/error) so the
// transition is spoken once; the rows themselves stay plain, otherwise a screen
// reader reels off every settled file on mount.
const useSettleAnnouncement = (files: UploadSlotFile[]): string => {
  const previousStatuses = useRef(new Map<string, UploadSlotFileStatus>());
  const [announcement, setAnnouncement] = useState("");
  useEffect(() => {
    const previous = previousStatuses.current;
    const next = new Map<string, UploadSlotFileStatus>();
    const settled: string[] = [];
    files.forEach(file => {
      const key = file.id ?? file.name;
      next.set(key, file.status);
      if (previous.get(key) === "uploading" && file.status !== "uploading") {
        settled.push(file.status === "done" ? `${file.name} uploaded` : `${file.name} failed to upload`);
      }
    });
    previousStatuses.current = next;
    if (settled.length > 0) {
      setAnnouncement(settled.join(", "));
    }
  }, [files]);
  return announcement;
};
type UploadSlotBaseProps = {
  /** The document being requested, e.g. "Photo ID". */
  title: string;
  /** Supporting line, e.g. "Driver's license or passport". */
  subtitle?: string;
  /** Marks the slot required with a trailing asterisk on the title. */
  required?: boolean;
  /** Validation state shared by the frame and its helper text. */
  validation?: FieldValidation;
  /** Helper or validation text associated with the composite slot. */
  helperText?: string;
  /** Files in the slot (controlled). Empty = nothing added yet. */
  files?: UploadSlotFile[];
  /** What the drop zone accepts. Defaults to "both". */
  accept?: UploadSlotAccept;
  /** The add action: open your source UI (file picker, photo/camera sheet,
   *  scanner), upload, and feed the result back via `files`. Without it, only
   *  drag-drop can add. */
  onAddPress?: () => void;
  /** Web drag-drop: the dropped files for you to upload. */
  onFilesDropped?: (files: File[]) => void;
  onRemove?: (file: UploadSlotFile, index: number) => void;
  onRetry?: (file: UploadSlotFile, index: number) => void;
  disabled?: boolean;
  testID?: string;
};

// `maxFiles` only means something alongside `multiple`, so the two are
// co-required: a cap without `multiple` is a type error, not a silent no-op.
//
// `answered` marks the slot settled by an answer rather than a file — the
// caller asked for a document and got a reason there isn't one. It is
// co-required with `action` for the same reason: the frame recedes and the add
// control gives way, so a slot answered with no way to take the answer back is
// a dead end, and an `action` on a slot still taking files would replace the
// add control with no way to add.
export type UploadSlotProps = UploadSlotBaseProps & ({
  multiple: true;
  maxFiles?: number;
} | {
  multiple?: false;
  maxFiles?: never;
}) & ({
  answered: true;
  /** Replaces the add control. Icon-only, so `label` is its accessible name. */
  action: {
    icon: IconComponent;
    label: string;
    onPress: () => void;
  };
} | {
  answered?: false;
  action?: never;
});
const normalizeMaxFiles = (maxFiles: number | undefined) => {
  if (maxFiles === undefined) {
    return undefined;
  }
  return Number.isFinite(maxFiles) ? Math.max(0, Math.floor(maxFiles)) : 0;
};
export const UploadSlot = ({
  title,
  subtitle,
  required = false,
  validation = "none",
  answered = false,
  action,
  helperText,
  files = [],
  multiple = false,
  maxFiles,
  accept = "both",
  onAddPress,
  onFilesDropped,
  onRemove,
  onRetry,
  disabled = false,
  testID
}: UploadSlotProps) => {
  const titleId = useId();
  const helperTextId = useId();
  const invalid = validation === "error";
  const normalizedMaxFiles = normalizeMaxFiles(maxFiles);
  const limit = multiple ? normalizedMaxFiles ?? Number.POSITIVE_INFINITY : 1;
  // Error rows don't hold a slot: a failed file can be retried in place or
  // replaced by a new pick, so it neither hides the add button, nor caps
  // drops, nor counts toward "n of m".
  const occupiedCount = files.filter(file => file.status !== "error").length;
  const remainingSlots = Math.max(0, limit - occupiedCount);
  const hasRoom = remainingSlots > 0;
  // Only a capped multi-file slot has a limit worth showing (single = always 1,
  // uncapped multiple = no ceiling to count against).
  const showCapacity = multiple && normalizedMaxFiles !== undefined;
  const canDrop = !disabled && !answered && hasRoom && onFilesDropped != null;
  const {
    isDragActive,
    dropHandlers
  } = useFileDrop({
    onFilesDropped: onFilesDropped ?? noop,
    accept,
    maxFiles: remainingSlots,
    disabled: !canDrop
  });
  const anyUploading = files.some(file => file.status === "uploading");
  const announcement = useSettleAnnouncement(files);
  return <YStack width="100%" data-sentry-element="YStack" data-sentry-component="UploadSlot" data-sentry-source-file="UploadSlot.tsx">
      <UploadSlotFrame role="group" aria-labelledby={titleId} aria-describedby={helperText ? helperTextId : undefined} dragActive={isDragActive} disabled={disabled} answered={answered} invalid={invalid} aria-busy={anyUploading} aria-invalid={invalid || undefined} testID={testID} {...dropHandlers} data-sentry-element="UploadSlotFrame" data-sentry-source-file="UploadSlot.tsx">
        <VisuallyHidden data-sentry-element="VisuallyHidden" data-sentry-source-file="UploadSlot.tsx">
          <YStack role="status" aria-live="polite" data-sentry-element="YStack" data-sentry-source-file="UploadSlot.tsx">
            {announcement ? <Caption>{announcement}</Caption> : null}
          </YStack>
        </VisuallyHidden>

        <XStack alignItems="center" gap="$sp2.5" data-sentry-element="XStack" data-sentry-source-file="UploadSlot.tsx">
          <YStack flex={1} data-sentry-element="YStack" data-sentry-source-file="UploadSlot.tsx">
            <SlotTitle id={titleId} muted={disabled || answered}
          // The asterisk is decorative (aria-hidden); fold "required" into
          // the title's accessible name so the composite group announces it.
          aria-label={required ? `${title}, required` : undefined} data-sentry-element="SlotTitle" data-sentry-source-file="UploadSlot.tsx">
              {title}
              {required ? <TextSpan color="$textNegative" aria-hidden>
                  {" "}
                  *
                </TextSpan> : null}
            </SlotTitle>
            {subtitle ? <Caption color="$textSubtle">{subtitle}</Caption> : null}
          </YStack>

          {action ? <Button variant="tertiary" size="small" icon={action.icon} fillWidth={false} aria-label={action.label} onPress={action.onPress} disabled={disabled} /> : showCapacity || hasRoom && onAddPress && !disabled ? <XStack alignItems="center" gap="$sp1.5" flexShrink={0}>
              {showCapacity ? <Caption color="$textSubtle">
                  {occupiedCount} of {normalizedMaxFiles}
                </Caption> : null}
              {hasRoom && onAddPress && !disabled ? <Button variant="secondary" size="small" iconLeft={PlusIcon} fillWidth={false}
          // Several slots can share a screen and the visual label is just
          // "Add"; the title disambiguates for screen readers.
          aria-label={`Add ${title}`} onPress={onAddPress}>
                  Add
                </Button> : null}
            </XStack> : null}
        </XStack>

        {files.length ? <YStack gap="$sp1">
            {files.map((file, index) => <FileRow key={file.id ?? `${file.name}-${index}`} file={file} index={index} onRemove={onRemove} onRetry={onRetry} disabled={disabled} />)}
          </YStack> : null}
      </UploadSlotFrame>
      <FieldHelperRow validation={validation} helperText={helperText} helperTextId={helperTextId} data-sentry-element="FieldHelperRow" data-sentry-source-file="UploadSlot.tsx" />
    </YStack>;
};